import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import TopBar from "@/components/TopBar";
import WhatsAppFab from "@/components/WhatsAppFab";
import AdminContactsSection from "@/components/AdminContactsSection";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum } from "@/utils/toEnglishDigits";
import {
  ArrowLeft,
  CalendarBlank,
  Plus,
  Info,
  Users,
  Car as CarIcon,
  ImageSquare,
  Eye,
} from "@phosphor-icons/react";

function isRealImage(url) {
  if (!url || typeof url !== "string") return false;
  if (!url.startsWith("data:image/") && !/^https?:\/\//.test(url)) return false;
  // Tiny 1x1 placeholder base64s are shorter than ~500 chars — treat them as missing.
  if (url.startsWith("data:image/") && url.length < 500) return false;
  return true;
}

function CarCard({ car, t }) {
  const img = isRealImage(car.image_url) ? car.image_url : null;
  const galleryCount = (car.gallery || car.images || []).length;
  const passengers = car.max_passengers ?? car.capacity ?? 4;
  return (
    <Link
      to={`/car/${encodeURIComponent(car.id)}`}
      data-testid={`fleet-card-${car.id}`}
      className="fadeup group relative block w-[200px] shrink-0 md:w-auto md:shrink h-[240px] rounded-2xl overflow-hidden border border-primary/30 bg-gradient-to-br from-[#172956] to-[#0F2148] hover:border-primary/70 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.4)] transition-all cursor-pointer"
    >
      {img ? (
        <img
          src={img}
          alt={car.name_ar}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a2f5e] via-[#0F2148] to-[#0A1936]">
          <CarIcon size={72} weight="duotone" className="text-primary/40" />
        </div>
      )}

      {/* bottom gradient scrim */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {/* Top-left tag: gallery count */}
      {galleryCount > 0 && (
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white text-[10px] font-semibold">
          <ImageSquare size={10} weight="fill" />
          <span className="num">{fmtNum(galleryCount)}</span> {t("home.carPhotos")}
        </div>
      )}
      {/* Top-right pill: تفاصيل (100% visible on every card) */}
      <div
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/20 backdrop-blur border border-primary/60 text-primary text-[10px] font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
        data-testid={`fleet-card-${car.id}-details`}
      >
        <Eye size={10} weight="fill" /> {t("home.carDetails")}
      </div>

      {/* Bottom info block */}
      <div className="absolute inset-x-0 bottom-0 p-3 z-10">
        <div className="text-primary font-bold text-lg leading-tight drop-shadow">{car.name_ar}</div>
        <div className="text-white/85 text-[11px] mt-0.5 flex items-center gap-1">
          <Users size={11} weight="fill" className="text-primary/80" />
          <span className="num">{fmtNum(passengers)}</span> {t("home.carPassengers")}
        </div>
      </div>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="fadeup flex-1 min-w-0 group flex flex-col items-center gap-2 rounded-2xl px-4 py-5 border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] hover:border-primary/70 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.4)] transition-all"
    >
      <div className="w-11 h-11 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center group-hover:bg-primary/25 transition-all">
        <Icon size={22} weight="duotone" className="text-primary" />
      </div>
      <span className="text-white text-sm font-semibold">{label}</span>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cars");
        if (!cancelled) setCars(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) toast.error(t("home.fleetLoadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const activeCars = useMemo(
    () => (cars || []).filter((c) => c.active !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [cars],
  );

  const firstName = (user?.name || "").split(" ")[0] || t("home.greetingDefault");

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="px-4 py-6" data-testid="home-page">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* 1) Welcome header */}
          <section className="space-y-1">
            <p className="text-white/70 text-sm md:text-base">{t("home.greeting")}، {firstName}</p>
            <h1 className="text-3xl md:text-5xl font-bold text-primary tracking-tight">{t("home.brand")}</h1>
          </section>

          {/* 2) Hero banner — luxury car photo (Unsplash, no license needed) */}
          <section
            data-testid="home-hero"
            className="relative rounded-2xl overflow-hidden h-[240px] md:h-[320px] border border-primary/25 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A1936] via-[#0F2148] to-[#1a2f5e]" />
            {/* Luxury black SUV (Cadillac Escalade / GMC style) — Unsplash */}
            <img
              src="https://images.unsplash.com/photo-1631295868223-63265b40d9e4?auto=format&fit=crop&w=1920&q=80"
              alt={t("home.heroTitle")}
              loading="eager"
              className="absolute inset-0 w-full h-full object-cover opacity-75"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            {/* Gold radial highlight */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(900px 260px at 85% -10%, rgba(212,175,55,0.35), transparent 60%), radial-gradient(600px 220px at 15% 110%, rgba(212,175,55,0.18), transparent 60%)",
              }}
            />
            {/* Bottom scrim so text stays readable */}
            <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

            <div className="relative h-full flex flex-col justify-end p-5 md:p-8">
              <h2 className="text-white text-2xl md:text-4xl font-bold leading-tight drop-shadow">
                {t("home.heroTitle")}
              </h2>
              <p className="text-white/85 text-sm md:text-base mt-1">{t("home.heroSubtitle")}</p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate("/new-booking")}
                  data-testid="hero-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.7)] transition-all"
                >
                  {t("home.heroCta")} <ArrowLeft size={16} weight="bold" className={isEn ? "rotate-180" : ""} />
                </button>
              </div>
            </div>
          </section>

          {/* 3) Quick actions */}
          <section
            data-testid="home-quick-actions"
            className="flex flex-row items-stretch gap-3 flex-wrap"
          >
            <QuickAction
              to="/my-bookings"
              icon={CalendarBlank}
              label={t("home.quickMyBookings")}
              testId="qa-my-bookings"
            />
            <QuickAction
              to="/new-booking"
              icon={Plus}
              label={t("home.quickNewBooking")}
              testId="qa-new-booking"
            />
            {user && !user.is_admin && user.role !== "admin" && (
              <QuickAction
                to="/company/clients"
                icon={Users}
                label={t("home.quickMyClients")}
                testId="qa-my-clients"
              />
            )}
            {user && !user.is_admin && user.role !== "admin" && (
              <QuickAction
                to="/company/my-requests"
                icon={Info}
                label={t("home.quickMyRequests")}
                testId="qa-my-requests"
              />
            )}
            <QuickAction
              to="/about"
              icon={Info}
              label={t("home.quickAbout")}
              testId="qa-about"
            />
          </section>

          {/* 4) Fleet */}
          <section data-testid="home-fleet">
            <div className="flex items-end justify-between mb-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-primary">{t("home.fleetTitle")}</h2>
                <p className="text-white/60 text-sm mt-1">{t("home.fleetSubtitle")}</p>
              </div>
              <Link
                to="/admin/cars"
                className="hidden md:inline-flex items-center gap-1 text-primary/70 hover:text-primary text-sm"
              >
                {t("common.viewAll")} <ArrowLeft size={14} className={isEn ? "rotate-180" : ""} />
              </Link>
            </div>

            {loading ? (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="w-[200px] h-[240px] rounded-2xl bg-white/5 border border-primary/10 shrink-0 animate-pulse"
                  />
                ))}
              </div>
            ) : activeCars.length === 0 ? (
              <div className="p-10 rounded-2xl border border-primary/15 bg-[#0F2148] text-center text-white/50 text-sm">
                {t("home.fleetEmpty")}
              </div>
            ) : (
              <>
                {/* Mobile: horizontal scroll */}
                <div className="md:hidden flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-px-4 -mx-4 px-4">
                  {activeCars.map((c) => (
                    <div key={c.id} className="snap-start">
                      <CarCard car={c} t={t} />
                    </div>
                  ))}
                </div>
                {/* Desktop: 4-col grid */}
                <div className="hidden md:grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {activeCars.map((c) => (
                    <CarCard key={c.id} car={c} t={t} />
                  ))}
                </div>
              </>
            )}
          </section>

          {/* 5) Contact us — General Manager (call only) + Booking Manager (call + whatsapp) */}
          <AdminContactsSection
            title={t("home.contactsTitle")}
            subtitle={t("home.contactsSubtitle")}
            testId="home-contacts"
          />
        </div>
      </main>
      <WhatsAppFab prefillText={t("home.whatsappPrefill", { name: user?.name || "" })} />
    </div>
  );
}
